import {
  FindTeamQuery,
  CreateTeamParams,
  UpdateTeamParams,
  TeamDTO,
  GetTeamParams,
  FindUserTeamsQuery,
} from './dtos/team'
import Team from './models/Team'
import { IAMAPI } from './API'
import DataLoader from 'dataloader'
import { collection } from 'infra/collection'
import { PrimeTeamMember } from './TeamMemberStore'
import Member from './models/Member'
import { Observable } from 'rxjs'
import { CursorPaged, Id, PrimeProps } from 'types/definitions'
import { action } from 'mobx'
import { AxiosError } from 'axios'
import * as path from 'path'
import { PrimeWorkspace } from './WorkspaceStore'

/**
 * Prime team.
 */
export type PrimeTeam = (props: PrimeProps<TeamDTO, 'id'>) => Team | undefined

/**
 * Find user teams.
 */
export type FindUserTeams = (
  query: FindUserTeamsQuery,
) => Promise<CursorPaged<Team>>

export default class TeamStore {
  teams = collection<TeamDTO, Team>({
    create: () => new Team(this.primeTeamMember, this.primeWorkspace),
  })

  constructor(
    private api: IAMAPI,
    private primeWorkspace: PrimeWorkspace,
    private primeTeamMember: PrimeTeamMember,
    currentMember$: Observable<Member>,
  ) {
    currentMember$.subscribe(async () => {
      // this.teams.clear()
    })
  }

  /**
   * Member loader cache.
   */
  teamLoaderCache = new Map()

  /**
   * Data loader.
   */
  teamLoader = new DataLoader(this._fetchMany.bind(this), {
    cacheMap: this.teamLoaderCache,
  })

  /**
   * Prime team.
   */
  primeTeam: PrimeTeam = (props) => {
    if (props?.id) {
      this.fetchTeam(props.id)
    }
    return this.teams.set(props)
  }

  /**
   * Fetches a team.
   */
  async fetchTeam(teamId: Id) {
    return this.teamLoader.load(teamId)
  }

  /**
   * Fetches many teams.
   *
   * @param {*} ids
   */
  async fetchManyTeams(ids: Array<Id>) {
    return Promise.all(ids.map((id) => this.teamLoader.load(id)))
  }

  /**
   * Queries for teams.
   *
   * @param {*} params
   */
  async findTeams(params: FindTeamQuery) {
    const { data, ...result } = await this.api.teams.find(params)
    return {
      ...result,
      data: this.teams.set(data),
    }
  }

  /**
   * Creates a team.
   *
   * @param {*} payload
   */
  async createTeam(payload: CreateTeamParams): Promise<Team> {
    return this.api.teams.create(payload).then((data) => this.teams.set(data))
  }

  /**
   * Get a team.
   *
   * @param {*} payload
   */
  async getTeam(payload: GetTeamParams): Promise<Team> {
    return this.api.teams.get(payload).then((data) => this.teams.set(data))
  }

  /**
   * Updates a team.
   *
   * @param {*} payload
   */
  async updateTeam(teamId: string, payload: UpdateTeamParams) {
    return this.api.teams
      .update(payload, teamId)
      .then((data) => this.teams.set(data))
  }

  /**
   * Implementation of fetching many teams.
   *
   * @param {*} ids
   */
  private async _fetchMany(ids: readonly Id[]) {
    const result = await this.api.teams
      .getMany(ids.map((id) => ({ id: Id.some(id) ? id : '' })))
      .then((data) => this.teams.set(data))
    result.forEach((t, i) => {
      if (!t) {
        // Just because a team was not found does not mean it will never exist.
        this.teamLoader.clear(ids[i])
      }
    })

    return result
  }

  /**
   * Uploads a new avatar for the team.
   *
   * @param {*} file
   */
  async changeAvatar(teamId: string, file: File) {
    const maxSize = 1024 * 1024 * 10 // 10mb
    const ext = path.extname(file.name)
    const isSupported = /(jpe?g|png)/i.test(ext)
    if (!isSupported) {
      console.error(
        'We only support PNG, JPEG and JPG files for profile pictures.',
      )
      return
    }

    if (maxSize < file.size) {
      console.error('Max image size is 10mb.')
      return
    }
    await this.api.teams
      .changeAvatar(teamId, file)
      .then((data) => this.teams.set(data))
      .catch(
        action((err: AxiosError) => {
          console.error(err)
        }),
      )
  }

  async getUserTeams(query: FindUserTeamsQuery): Promise<CursorPaged<Team>> {
    return CursorPaged.map(await this.api.users.getTeams(query), this.teams.set)
  }
}
